import type { QuoteCurrency } from "features/market/lib/marketQuotes";
import type { PanelType } from "features/market/api/MarketQuotes";

const ULTIMO_KEY = "ÚltimoOperado";
const VARIACION_KEY = "VariaciónDiaria";
const MONTO_KEY = "MontoOperado";
const SYMBOL_KEY = "symbol";
const NAME_KEY = "name";

export interface CurrencyVariant {
  ticker: string;
  lastPrice: number | null;
  variation: number | null;
  volume: number | null;
}

export interface Quote {
  name: string;
  ARS?: CurrencyVariant;
  USD?: CurrencyVariant;
}

/**
 * Strip bi-monetary suffixes (C, D, DD) to get the base symbol for grouping.
 * e.g. AE38C → ae38, AE38D → ae38, YPFDD → ypf, AAPLD → aapl
 */
function symbolBaseKey(symbol: string): string {
  const s = (symbol ?? "").trim();
  return s.replace(/[CD]+$/i, "").toLowerCase();
}

/**
 * Compute a grouping key from name and symbol so bi-monetary variants
 * (e.g. AE38 / AE38C / AE38D) are merged into one Quote.
 */
function groupingKey(symbol: string, name: string, panel?: PanelType): string {
  if (panel === "cedears") {
    return symbolBaseKey(symbol) || (symbol ?? "").trim().toLowerCase();
  }

  const rawName = (name ?? "").trim().toLowerCase();
  const nameKey = rawName.replace(/^(cedear|etf cedear)\s+/i, "").trim();
  if (nameKey) return nameKey;
  return symbolBaseKey(symbol) || (symbol ?? "").trim().toLowerCase();
}

export function parseRowsToQuotes(
  rows: Record<string, string | number | null>[],
  panel?: PanelType
): Quote[] {
  const byKey = new Map<
    string,
    { displayName: string; variants: CurrencyVariant[] }
  >();

  for (const row of rows) {
    const name = row[NAME_KEY];
    const symbol = row[SYMBOL_KEY];
    if (name == null && symbol == null) continue;

    const symbolStr = String(symbol ?? "").trim();
    const symbolUpper = symbolStr.toUpperCase();

    if (panel === "cedears" && symbolUpper.endsWith("C") && !symbolUpper.endsWith(".C")) continue;

    const key = groupingKey(symbolStr, String(name ?? ""), panel);
    if (!key) continue;

    const lastPrice =
      typeof row[ULTIMO_KEY] === "number" ? row[ULTIMO_KEY] : null;
    const variation =
      typeof row[VARIACION_KEY] === "number" ? row[VARIACION_KEY] : null;
    const volume =
      typeof row[MONTO_KEY] === "number" ? row[MONTO_KEY] : null;

    const variant: CurrencyVariant = {
      ticker: symbolStr,
      lastPrice,
      variation,
      volume,
    };

    if (!byKey.has(key)) {
      byKey.set(key, {
        displayName: String(name ?? symbol ?? ""),
        variants: [],
      });
    }
    byKey.get(key)!.variants.push(variant);
  }

  const quotes: Quote[] = [];
  byKey.forEach((entry) => {
    const quote: Quote = { name: entry.displayName };
    const hasDD = entry.variants.some(
      (v) => v.ticker.toUpperCase().endsWith("DD")
    );

    for (const v of entry.variants) {
      const s = v.ticker.toUpperCase();
      if (s.endsWith("DD")) {
        quote.USD = v;
      } else if (s.endsWith("D") && !s.endsWith(".D")) {
        if (hasDD) {
          if (!quote.ARS) quote.ARS = v;
        } else {
          quote.USD = v;
        }
      } else if (s.endsWith(".D")) {
        quote.USD = v;
      } else if (s.endsWith("C") && !s.endsWith(".C")) {
        if (!quote.ARS) quote.ARS = v;
      } else if (s.endsWith(".C")) {
        if (!quote.ARS) quote.ARS = v;
      } else {
        quote.ARS = v;
      }
    }
    quotes.push(quote);
  });

  return quotes;
}

export function getTickerForCurrency(
  quote: Quote,
  pref: QuoteCurrency
): CurrencyVariant | null {
  const match = pref === "ARS" ? quote.ARS : quote.USD;
  return match ?? quote.ARS ?? quote.USD ?? null;
}

export interface FlattenedQuoteForAdd {
  symbol: string;
  name: string;
  defaultPrice: number | null;
  panel: PanelType;
}

export function flattenQuotesForAddDialog(
  quotes: Quote[],
  panel: PanelType
): FlattenedQuoteForAdd[] {
  const result: FlattenedQuoteForAdd[] = [];
  for (const quote of quotes) {
    if (quote.ARS) {
      result.push({
        symbol: quote.ARS.ticker,
        name: quote.name,
        defaultPrice: quote.ARS.lastPrice,
        panel,
      });
    }
    if (quote.USD) {
      result.push({
        symbol: quote.USD.ticker,
        name: quote.name,
        defaultPrice: quote.USD.lastPrice,
        panel,
      });
    }
  }
  return result;
}

export function buildSymbolToPriceFromQuotes(
  quotes: Quote[]
): Map<string, number> {
  const map = new Map<string, number>();
  for (const quote of quotes) {
    if (quote.ARS?.lastPrice != null)
      map.set(quote.ARS.ticker, quote.ARS.lastPrice);
    if (quote.USD?.lastPrice != null)
      map.set(quote.USD.ticker, quote.USD.lastPrice);
  }
  return map;
}

export function buildSymbolToVariationFromQuotes(
  quotes: Quote[]
): Map<string, number> {
  const map = new Map<string, number>();
  for (const quote of quotes) {
    if (quote.ARS?.variation != null)
      map.set(quote.ARS.ticker, quote.ARS.variation);
    if (quote.USD?.variation != null)
      map.set(quote.USD.ticker, quote.USD.variation);
  }
  return map;
}
